import React, { useEffect, useState } from 'react';
import API from '../../services/api';
import { usePopup } from '../../context/PopupContext';
import { UPDATE_CATEGORY_LABEL } from './PlatformUpdates';
import PaginationControls from '../../components/ui/PaginationControls';
import '../admin/Analytics.css';
import '../../components/admin/AnnouncementEditor.css';

const PAGE_SIZE = 25;

const blank = { title: '', body: '', category: 'update', college_id: '', pinned: false, expires_at: '' };

function UpdateForm({ initial, colleges, onSave, onCancel }) {
  const [f, setF] = useState(() => ({ ...blank, ...(initial ? { ...initial, college_id: initial.college_id || '', expires_at: initial.expires_at ? initial.expires_at.slice(0, 10) : '' } : {}) }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await onSave({ title: f.title.trim(), body: f.body.trim(), category: f.category, pinned: f.pinned, college_id: f.college_id || null, expires_at: f.expires_at ? new Date(f.expires_at).toISOString() : null });
    } catch (ex) { setErr(ex.response?.data?.message || 'Could not save this update.'); }
    finally { setSaving(false); }
  };

  return (
    <form className="ann-editor" onSubmit={submit}>
      <label className="ann-field"><span>Title</span><input value={f.title} onChange={set('title')} required maxLength={140} /></label>
      <label className="ann-field"><span>Message</span><textarea value={f.body} onChange={set('body')} required rows={4} maxLength={4000} /></label>
      <div className="ann-field-row">
        <label className="ann-field"><span>Type</span>
          <select value={f.category} onChange={set('category')}>{Object.entries(UPDATE_CATEGORY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </label>
        <label className="ann-field"><span>Audience</span>
          <select value={f.college_id} onChange={set('college_id')} disabled={!!initial}>
            <option value="">All colleges</option>
            {colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="ann-field"><span>Expires (optional)</span><input type="date" value={f.expires_at} onChange={set('expires_at')} /></label>
      </div>
      <label className="ann-check"><input type="checkbox" checked={f.pinned} onChange={set('pinned')} /> Pin to the top</label>
      {err && <div className="axh-error" role="alert">{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="axh-btn primary" disabled={saving}>{saving ? 'Saving…' : initial ? 'Save changes' : 'Publish'}</button>
        <button type="button" className="axh-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// Super-admin authoring of platform-level communications to college admins.
export default function SuperAdminUpdates() {
  const { showConfirm } = usePopup();
  const [items, setItems] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [editing, setEditing] = useState(null);   // null | 'new' | update
  const [err, setErr] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });

  const load = (p = page) => {
    setErr('');
    Promise.allSettled([API.get('/platform-updates', { params: { page: p, limit: PAGE_SIZE } }), API.get('/colleges')]).then(([u, c]) => {
      if (u.status === 'fulfilled') { setItems(u.value.data.data || []); setMeta(u.value.data.metadata || { total: 0, total_pages: 1 }); }
      else { setItems([]); setErr(u.reason?.response?.data?.message || 'We could not load platform updates.'); }
      if (c.status === 'fulfilled') setColleges(c.value.data || []);
    });
  };
  useEffect(() => load(page), [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (payload) => {
    if (editing === 'new') await API.post('/platform-updates', payload);
    else { const { college_id, ...rest } = payload; await API.put(`/platform-updates/${editing.id}`, rest); }
    setEditing(null); load();
  };
  const remove = async (u) => {
    if (!(await showConfirm('Delete update', `Delete "${u.title}"? College admins will no longer see it.`))) return;
    await API.delete(`/platform-updates/${u.id}`); load();
  };

  return (
    <div className="axh-page">
      <div className="axh-topbar">
        <div><h1>Platform Updates</h1><p>Communicate with college admins — releases, maintenance and notices. Students never see these.</p></div>
        <div className="axh-topbar-right">
          <button type="button" className="axh-btn primary" aria-expanded={editing === 'new'} onClick={() => setEditing(cur => (cur === 'new' ? null : 'new'))}>
            {editing === 'new' ? 'Close form' : '+ New update'}
          </button>
        </div>
      </div>

      {editing && (
        <div className="axh-card" style={{ marginBottom: 20 }}>
          <h3>{editing === 'new' ? 'New platform update' : 'Edit platform update'}</h3>
          <UpdateForm key={editing === 'new' ? 'new' : editing.id} initial={editing === 'new' ? null : editing} colleges={colleges} onSave={save} onCancel={() => setEditing(null)} />
        </div>
      )}

      {err && <div className="axh-error" role="alert">{err}</div>}
      {items === null && <div className="axh-loading"><div className="axh-spinner" />Loading…</div>}
      {items && items.length === 0 && !err && <div className="axh-card"><div className="axh-empty">No platform updates yet — publish one to reach college admins.</div></div>}
      {items && items.length > 0 && (
        <div className="axh-grid-2">
          {items.map(u => (
            <div className="axh-card" key={u.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span className="axh-pill" style={{ background: 'var(--z-info-tint)', color: 'var(--z-violet)' }}>{UPDATE_CATEGORY_LABEL[u.category] || u.category}</span>
                {u.pinned && <span className="axh-badge" style={{ background: 'var(--z-warning-tint)', color: 'var(--z-warning)' }}>Pinned</span>}
              </div>
              <h3 style={{ marginBottom: 6 }}>{u.title}</h3>
              <p style={{ fontSize: '0.84rem', color: 'var(--z-muted)', marginBottom: 10, whiteSpace: 'pre-wrap' }}>{u.body}</p>
              <p style={{ fontSize: '0.74rem', color: 'var(--z-muted-2)', marginBottom: 12 }}>{u.audience || 'All colleges'}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="axh-btn" onClick={() => setEditing(u)}>Edit</button>
                <button type="button" className="axh-btn" style={{ color: 'var(--z-danger)' }} onClick={() => remove(u)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {items && items.length > 0 && <PaginationControls page={page} totalPages={meta.total_pages} total={meta.total} onChange={setPage} />}
    </div>
  );
}
